import styled from 'styled-components'

import { media } from 'featureSliced/shared/utils/breakpoints'
import { getContrastColor } from 'featureSliced/shared/utils/getContrastColor'

type ItemProps = {
  isSelected: boolean
  count?: number | string
}

export const ItemsContainer = styled.div`
  position: relative;
  overflow-y: scroll;
  max-height: calc(100vh - 334px);
  margin-bottom: 10px;

  /* the web dialog scrolls itself — an inner viewport-derived cap only forces
     a second scrollbar around the variant grid */
  ${media.web} {
    overflow-y: visible;
    max-height: none;
  }
`

export const ItemsGrid = styled.div`
  overflow-y: scroll;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-auto-rows: minmax(120px, auto);
  align-content: start;
  grid-gap: 10px;
  position: relative;
  &:after {
    content: unset;
  }

  /* one sub-article per row: allergens and the customer note sit inline,
     ingredients/nutrients expand under the row */
  ${media.web} {
    overflow-y: visible;
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
  }
`
export const ItemsGridExpander = styled.div`
  position: absolute;
  background-image: linear-gradient(
    transparent,
    ${({ theme }) => theme.MainBackGround}
  );
  position: absolute;
  left: 0;
  bottom: -1px;
  width: 100%;
  height: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  &:after {
    content: unset;
  }
`

export const MainTitle = styled.div`
  text-align: center;
  margin: 10px 0;
  color: ${({ theme }) => `${theme.FontColor}`};
`
export const Item = styled('div')<ItemProps>`
  min-height: 120px;
  border: ${({ theme, isSelected }) =>
    isSelected
      ? `1px solid ${theme.colour || '#34CB9E'}`
      : `1px solid ${theme.ItemsBorders}`};

  border-radius: 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  position: relative;

  background-color: ${({ theme }) => theme.ItemsBackGround};

  &:after {
    content: ${({ count }) => (count ? `'${count}'` : 'unset')};
    color: #fff;
    font-size: 12px !important;
    position: absolute;
    top: 0;
    right: 0;
    background-color: ${({ theme }) => theme.colour};
    height: 25px;
    /* width: 25px; */
    padding: 0 11px;
    border-radius: 0 0 0 13px;
    text-align: center;
    line-height: 25px;
  }
  p {
    margin: 0;
    font-weight: 500;
    font-size: 10px !important;
    line-height: 18px;
    color: #434343;
    text-align: center;
  }

  ${media.web} {
    min-height: 0;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 0 12px;
    padding: 8px 12px;
    p {
      text-align: left;
      font-size: 13px !important;
    }
  }
`

// sized to match ItemAllergen (16px) and filled with the venue accent, per
// Pavel's review 2026-09-21 — the phone corner icon (top-right, mirrors the
// selection count badge's corner) and the web title-row icon share this one
export const ItemInfoIcon = styled.button`
  width: 16px;
  height: 16px;
  min-width: 16px;
  border: none;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: ${({ theme }) => theme.colour || '#34CB9E'};
  color: ${({ theme }) => getContrastColor(theme.colour || '34CB9E')};

  svg {
    width: 9px;
    height: 9px;
    display: block;
  }
`

export const ItemInfoIconCorner = styled(ItemInfoIcon)`
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 1;
`

export const ItemAllergens = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
  padding: 2px 6px 6px;
  ${media.web} {
    justify-content: flex-start;
    padding: 0;
  }
`

export const ItemAllergen = styled.img`
  background: #ff5151;
  height: 16px;
  width: 16px;
  object-fit: cover;
  border-radius: 5px;
`

export const ItemBody = styled.div`
  flex: 0 1 auto;
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;

  /* the 200px basis only makes sense once Item is a row (web); on phone
     (Item is a column) it was read as a height and blew the tile up to
     ~290px */
  ${media.web} {
    flex: 1 1 200px;
    width: auto;
  }
`

// note text now lives only inside ItemDetails (the opening block) via
// ProductInfoModal's own note section — no separate always-visible ItemNote
export const ItemDetails = styled.div`
  /* rendered as a SIBLING of Item, not a child — Item's own \`p\` rule
     (dark grey, forced 10/13px !important, scoped to the tile's title/price)
     is a descendant selector, so keeping the info body out of Item's DOM
     subtree is what actually isolates it, without fighting that rule's
     specificity/!important from the outside */
  grid-column: 1 / -1;
  padding: 0 12px 10px;
  text-align: left;
  p {
    text-align: left;
  }
`

// always round, real photo or the camera-icon placeholder alike (Pavel,
// 2026-09-21) — both phone tiles and web rows
export const ItemImageContainer = styled.div`
  height: 60px;
  min-height: 60px;
  width: 60px;
  position: relative;
  box-sizing: border-box;
  /* background: #b0b0b0; */
  border-radius: 100%;
  overflow: hidden;
  margin: 12px 0 0;
`

export const ItemImage = styled.img`
  height: 100%;
  object-fit: contain;
  min-height: 100%;
  min-width: 100%;
`

export const ItemPrice = styled.p`
  color: ${({ theme }) => `${theme.FontColor} !important`};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
`

export const ItemTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`

// flex-grow:0 on purpose — growing to fill the row (as a plain "flex:1"
// truncating label normally would) stretched the title's own box past its
// visible glyphs, so the icon sibling rendered far to the right instead of
// right after the text (Pavel, 2026-09-21). Shrinks (and truncates, via the
// inherited line-clamp) when the row is tight, hugs its content otherwise.
export const ItemTitleText = styled(ItemPrice)`
  flex: 0 1 auto;
  min-width: 0;
`

// --- step navigation (QRM-664) ------------------------------------------------
// One node per choosable step, the way the Smart_Checkout draws its StepProgressView:
// number in a circle, the template's name under it, a check once the step is done,
// the accent on the current one. Every node is a button — any direction, any time.

type StepNodeCircleProps = {
  isCurrent: boolean
  isDone: boolean
}

type StepLabelProps = {
  isCurrent: boolean
}

export const StepRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 0;
  margin: 0 0 6px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

export const StepNode = styled.button`
  flex: 0 0 auto;
  width: 72px;
  min-width: 56px;
  border: none;
  background: transparent;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  position: relative;
  color: ${({ theme }) => theme.FontColor};

  /* connector to the next node */
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 11px;
    left: calc(50% + 12px);
    width: calc(100% - 24px);
    height: 2px;
    background: ${({ theme }) => theme.ItemsBorders || '#cdcdcd'};
  }
`

export const StepNodeCircle = styled('div')<StepNodeCircleProps>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  box-sizing: border-box;
  border: 2px solid
    ${({ theme, isCurrent, isDone }) =>
      isCurrent || isDone
        ? theme.colour || '#34CB9E'
        : theme.ItemsBorders || '#cdcdcd'};
  background: ${({ theme, isCurrent }) =>
    isCurrent ? theme.colour || '#34CB9E' : theme.ItemsBackGround || '#fff'};
  color: ${({ theme, isCurrent, isDone }) =>
    isCurrent
      ? getContrastColor(theme.colour || '34CB9E')
      : isDone
      ? theme.colour || '#34CB9E'
      : theme.FontColor};
`

export const StepLabel = styled('span')<StepLabelProps>`
  margin-top: 4px;
  max-width: 100%;
  padding: 0 2px;
  font-size: 10px;
  line-height: 12px;
  text-align: center;
  font-weight: ${({ isCurrent }) => (isCurrent ? 600 : 400)};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
`

export const StepStatus = styled.div`
  display: flex;
  justify-content: center;
  gap: 14px;
  margin: -4px 0 8px;
  font-size: 11px;
  color: ${({ theme }) => theme.FontColor};
  opacity: 0.8;
`

// top-left twin of the count badge (top-right): one tap takes a unit out again
export const ItemRemove = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  height: 25px;
  min-width: 25px;
  padding: 0 8px;
  border: none;
  border-radius: 0 0 13px 0;
  background: ${({ theme }) => theme.colour || '#34CB9E'};
  color: ${({ theme }) => getContrastColor(theme.colour || '34CB9E')};
  font-size: 16px;
  line-height: 25px;
  cursor: pointer;
`
